import { Head, router, usePage } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import { VoicePicker } from "@/components/agents/setup/receptionist/voice-picker"
import { AddJobModal } from "@/components/billing/add-job-modal"
import { OnboardingShell } from "@/components/onboarding/shell"
import { StepProfile } from "@/components/onboarding/step-profile"
import { StepSearch } from "@/components/onboarding/step-search"
import {
  type OnboardingPageProps,
  STEP_ORDER,
  type Step,
  detectDomain,
  emptyProfile,
  stepForAccount,
} from "@/components/onboarding/types"
import type { PlacesPickerSelection } from "@/components/places-picker"
import { AgentMark, markForSlug } from "@/components/ui/agent-mark"
import { Button } from "@/components/ui/button"
import { useAssistant } from "@/hooks/use-assistant"
import { cn } from "@/lib/utils"
import {
  onboardingPickAgentPath,
  onboardingSkipPath,
  onboardingVoicePath,
} from "@/routes"

// IMP-457: the new account onboarding — the client meets the assistant, picks
// its voice, confirms the business profile, then sees what it can do. The orb
// stays in frame throughout (see OnboardingShell). Both provisioning paths
// start with the intro; pre-provisioned accounts skip search + scrape and go
// straight to profile confirmation.

const ANALYZING_HOLD_LINE = "Almost there…"

export default function Onboarding() {
  const {
    business_profile,
    account,
    jobs,
    voices,
    industry_options,
    truncated,
  } = usePage().props as unknown as OnboardingPageProps
  const { name: assistantName, theme } = useAssistant()
  const partner = usePage().props.partner as
    | { name?: string; billing_type?: string; stripe_payouts_ready?: boolean }
    | undefined
  const imperativeManaged = partner?.billing_type === "imperative_managed"

  const serverStep = stepForAccount(account.onboarding_step)
  const [step, setStep] = useState<Step>(serverStep)
  const [voiceId, setVoiceId] = useState(voices[0]?.id ?? "")
  const [savingVoice, setSavingVoice] = useState(false)
  const [analyzeLine, setAnalyzeLine] = useState(0)
  // IMP-465: buying a job from the jobs screen happens in the add-job modal,
  // right in the flow. The purchase completes onboarding server-side.
  const [addingUid, setAddingUid] = useState<string | null>(null)
  const [analyzing, setAnalyzing] = useState<{
    name: string
    domain: string | null
  }>({ name: "", domain: null })

  // Sync forward only when the SERVER step actually advances (a POST moved
  // it). Comparing against the local step on every render instead would snap
  // a deliberate "Go back" (local behind the server) straight back forward —
  // Chris's IMP-457 report.
  const lastServerStep = useRef(serverStep)
  useEffect(() => {
    if (lastServerStep.current === serverStep) return
    lastServerStep.current = serverStep
    const idx = STEP_ORDER.indexOf(serverStep)
    const localIdx = STEP_ORDER.indexOf(step)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (idx > localIdx) setStep(serverStep)
  }, [serverStep, step])

  const analyzingLines = [
    analyzing.domain ? `Reading ${analyzing.domain}…` : "Reading your website…",
    "Checking your business listings…",
    "Learning your services, hours, and brand…",
  ]

  // Analyzing: cycle the status lines while the scrape request completes; the
  // server redirect advances the step when it's done.
  useEffect(() => {
    if (step !== "analyzing") return
    if (analyzeLine >= analyzingLines.length) return
    const t = setTimeout(() => setAnalyzeLine((n) => n + 1), 1100)
    return () => clearTimeout(t)
  }, [step, analyzeLine, analyzingLines.length])

  function handleVoiceContinue() {
    setSavingVoice(true)
    router.patch(
      onboardingVoicePath(),
      { account: { assistant_voice_id: voiceId } },
      { onFinish: () => setSavingVoice(false) },
    )
  }

  function handleSelect(b: PlacesPickerSelection) {
    setAnalyzing({ name: b.name, domain: detectDomain(b.website ?? "") })
    setAnalyzeLine(0)
    setStep("analyzing")
    router.post(
      "/onboarding/select_business",
      {
        place_id: b.place_id,
        name: b.name,
        website: b.website ?? "",
        address: b.address ?? "",
      },
      // Advance explicitly: on a re-scan after "Go back" the server step is
      // already "details", so the server-advance sync above won't fire.
      { preserveScroll: true, onSuccess: () => setStep("profile") },
    )
  }

  function handleNoWebsite() {
    router.post(
      "/onboarding/skip_scrape",
      {},
      {
        onSuccess: () => setStep("profile"),
      },
    )
  }

  const purchasedCount = jobs.filter((j) => j.purchased).length

  return (
    <OnboardingShell step={step}>
      <Head title="Welcome" />

      {step === "intro" && (
        <div
          key="intro"
          className="animate-in fade-in slide-in-from-bottom-2 space-y-8 text-center duration-500"
        >
          <div className="space-y-3">
            <h1 className="text-2xl font-semibold">
              Hi, I&rsquo;m {assistantName}
              <span style={{ color: theme.swatch }}>.</span>
            </h1>
            <p className="text-muted-foreground mx-auto max-w-md text-sm leading-relaxed">
              I answer phones, follow up with leads, and chat with your website
              visitors. Give me a few minutes and I&rsquo;ll be working for you
              today.
            </p>
          </div>
          <Button size="lg" onClick={() => setStep("voice")}>
            Let&rsquo;s get started
          </Button>
        </div>
      )}

      {step === "voice" && (
        <div
          key="voice"
          className="animate-in fade-in slide-in-from-bottom-2 space-y-8 duration-500"
        >
          <div className="space-y-2 text-center">
            <h1 className="text-xl font-semibold">How should I sound?</h1>
            <p className="text-muted-foreground text-sm">
              This is how I&rsquo;ll sound to your callers and leads — and to
              you, whenever we talk.
            </p>
          </div>
          <VoicePicker
            voices={voices.slice(0, 6)}
            selected={voiceId}
            onSelect={setVoiceId}
          />
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={handleVoiceContinue}
              loading={savingVoice}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === "business" && (
        <StepSearch onSelect={handleSelect} onNoWebsite={handleNoWebsite} />
      )}

      {step === "analyzing" && (
        <div
          key="analyzing"
          className="animate-in fade-in space-y-6 text-center duration-500"
        >
          <h1 className="text-xl font-semibold">
            Getting to know {analyzing.name || "your business"}
          </h1>
          <div className="space-y-2">
            {analyzingLines.slice(0, analyzeLine + 1).map((line, i) => (
              <p
                key={line}
                className={cn(
                  "animate-in fade-in text-sm duration-300",
                  i === analyzeLine
                    ? "text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {line}
              </p>
            ))}
            {analyzeLine >= analyzingLines.length && (
              <p className="animate-in fade-in text-sm duration-300">
                {ANALYZING_HOLD_LINE}
              </p>
            )}
          </div>
        </div>
      )}

      {step === "profile" && (
        <StepProfile
          profile={business_profile ?? emptyProfile()}
          industryOptions={industry_options}
          truncated={truncated}
          onBack={() => setStep("business")}
        />
      )}

      {step === "jobs" && (
        <div
          key="jobs"
          className="animate-in fade-in slide-in-from-bottom-2 space-y-6 duration-500"
        >
          <div className="space-y-2 text-center">
            <h1 className="text-xl font-semibold">
              Here&rsquo;s what I can do
            </h1>
            <p className="text-muted-foreground text-sm">
              {purchasedCount > 0
                ? `${purchasedCount === 1 ? "One job is" : `${purchasedCount} jobs are`} in your plan and ready to turn on.`
                : "Pick a job and let’s get started."}
            </p>
          </div>

          {/* The CTA sits beside the copy from `sm` up and drops below it on a
              phone: between a shrink-0 mark and a shrink-0 button, the
              description was squeezed to one word per line. */}
          <div className="space-y-3">
            {jobs.map((job) => (
              <div
                key={job.uid}
                className="bg-background flex flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-start"
              >
                <div className="flex flex-1 items-start gap-4">
                  <AgentMark
                    kind={markForSlug(job.slug)}
                    className={cn(
                      "size-12 shrink-0",
                      job.purchased
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{job.name}</p>
                    {job.description && (
                      <p className="text-muted-foreground mt-0.5 text-sm">
                        {job.description}
                      </p>
                    )}
                  </div>
                </div>
                {job.purchased ? (
                  <Button
                    className="shrink-0"
                    onClick={() =>
                      router.post(onboardingPickAgentPath({ id: job.uid }))
                    }
                  >
                    Get started
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={() => setAddingUid(job.uid)}
                  >
                    {job.retail_price_cents != null
                      ? `Add for $${Math.round(job.retail_price_cents / 100)}/mo`
                      : "Get started"}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              type="button"
              variant="ghost"
              onClick={() => router.post(onboardingSkipPath())}
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              I&rsquo;ll do this later
            </Button>
          </div>
        </div>
      )}

      <AddJobModal
        archetypeUid={addingUid}
        onClose={() => setAddingUid(null)}
        billingBlocked={
          imperativeManaged && partner?.stripe_payouts_ready !== true
        }
        partnerName={partner?.name ?? null}
        imperativeManaged={imperativeManaged}
      />
    </OnboardingShell>
  )
}
